import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

interface SettingMap {
  orgName: string; orgFullName: string; university: string; tagline: string;
  mission: string; vision: string; aboutContent: string;
  address: string; email: string; phone: string;
  facebook: string; tiktok: string; instagram: string; linkedin: string;
  pillars: string; services: string;
}

const DEFAULT_SETTINGS: SettingMap = {
  orgName: "", orgFullName: "", university: "", tagline: "",
  mission: "", vision: "", aboutContent: "",
  address: "", email: "", phone: "",
  facebook: "", tiktok: "", instagram: "", linkedin: "",
  pillars: "[]", services: "[]",
};

const SETTING_KEYS = Object.keys(DEFAULT_SETTINGS);

export default function SettingsManager() {
  const [settings, setSettings] = useState<SettingMap>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      const { data } = await supabase.from("site_settings").select("key, value");
      const map = { ...DEFAULT_SETTINGS };
      (data || []).forEach((s) => { if (s.key in map) map[s.key as keyof SettingMap] = s.value; });
      setSettings(map);
      setIsLoading(false);
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const updates = SETTING_KEYS.map((key) => ({
      key,
      value: settings[key as keyof SettingMap],
    }));
    // Upsert all settings
    const { error } = await supabase.from("site_settings").upsert(updates, { onConflict: "key" });
    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
    setSaving(false);
  };

  const updateSetting = (key: keyof SettingMap, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-96 bg-gray-100 rounded-xl max-w-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl text-primary-900">Site Settings</h1>
        {saved && (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg">
            <i className="ri-check-line" /> Saved successfully
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-100 max-w-3xl">
        {/* Organization Info */}
        <div className="p-5 md:p-6">
          <h3 className="font-heading font-semibold text-base text-primary-900 mb-4">Organization Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Organization Name</label><input type="text" value={settings.orgName} onChange={(e) => updateSetting("orgName", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label><input type="text" value={settings.orgFullName} onChange={(e) => updateSetting("orgFullName", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">University</label><input type="text" value={settings.university} onChange={(e) => updateSetting("university", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Tagline</label><input type="text" value={settings.tagline} onChange={(e) => updateSetting("tagline", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="p-5 md:p-6">
          <h3 className="font-heading font-semibold text-base text-primary-900 mb-4">Mission & Vision</h3>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Mission</label><textarea value={settings.mission} onChange={(e) => updateSetting("mission", e.target.value)} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Vision</label><textarea value={settings.vision} onChange={(e) => updateSetting("vision", e.target.value)} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">About Content</label><textarea value={settings.aboutContent} onChange={(e) => updateSetting("aboutContent", e.target.value)} rows={5} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none" /></div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="p-5 md:p-6">
          <h3 className="font-heading font-semibold text-base text-primary-900 mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label><input type="text" value={settings.address} onChange={(e) => updateSetting("address", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label><input type="email" value={settings.email} onChange={(e) => updateSetting("email", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label><input type="text" value={settings.phone} onChange={(e) => updateSetting("phone", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
          </div>
        </div>

        {/* Social Media */}
        <div className="p-5 md:p-6">
          <h3 className="font-heading font-semibold text-base text-primary-900 mb-4">Social Media Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Facebook</label><input type="text" value={settings.facebook} onChange={(e) => updateSetting("facebook", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Tiktok</label><input type="text" value={settings.tiktok} onChange={(e) => updateSetting("tiktok", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Instagram</label><input type="text" value={settings.instagram} onChange={(e) => updateSetting("instagram", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1.5">LinkedIn</label><input type="text" value={settings.linkedin} onChange={(e) => updateSetting("linkedin", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" /></div>
          </div>
        </div>

        {/* Pillars */}
        <div className="p-5 md:p-6">
          <h3 className="font-heading font-semibold text-base text-primary-900 mb-4">Strategic Pillars (JSON)</h3>
          <textarea value={settings.pillars} onChange={(e) => updateSetting("pillars", e.target.value)} rows={6} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none font-mono text-xs" />
        </div>

        {/* Services */}
        <div className="p-5 md:p-6">
          <h3 className="font-heading font-semibold text-base text-primary-900 mb-4">Services (JSON)</h3>
          <textarea value={settings.services} onChange={(e) => updateSetting("services", e.target.value)} rows={6} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none font-mono text-xs" />
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 bg-gold-600 hover:bg-gold-700 text-white font-medium rounded-lg transition-colors text-sm disabled:opacity-60">
          {saving ? "Saving..." : "Save Changes"}
        </button>
        <button onClick={() => setSettings(DEFAULT_SETTINGS)} className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm">Reset</button>
      </div>
    </div>
  );
}